import React from "react";
import { PublicationSocialCard } from "./PublicationSocialCard";
import { HstvCard } from "./HstvCard";
import { TwitterStatsCard } from "./TwitterStatsCard";
import { useFacebookStats } from "../../hooks/useFacebookStats";
import { useInstagramStats } from "../../hooks/useInstagramStats";

const PUB_COLORS = {
  newsday: "#2563EB", standard: "#059669", zimind: "#7C3AED", southerneye: "#0891B2",
  hstv: "#DC2626", icwt: "#f59e0b",
};
const PUB_LABELS = {
  newsday: "NewsDay", standard: "The Standard", zimind: "Zimbabwe Independent",
  southerneye: "Southern Eye", hstv: "HSTV", icwt: "ICWT",
};

export function SocialOverviewGrid({ socialData, socialRealtime, twitterByPub, loading, error, isMobile, isTablet, theme: t }) {
  const publications = socialData?.publications ?? [];
  const byPublication = socialRealtime?.byPublication ?? {};

  const newsdayFb     = useFacebookStats(process.env.REACT_APP_FB_NEWSDAY_PAGE_ID);
  const southerneyeFb = useFacebookStats(process.env.REACT_APP_FB_SOUTHERNEYE_PAGE_ID);
  const zimindFb      = useFacebookStats(process.env.REACT_APP_FB_ZIMINDEPENDENT_PAGE_ID);
  const standardFb    = useFacebookStats(process.env.REACT_APP_FB_THESTANDARD_PAGE_ID);
  const newsdayIg = useInstagramStats(
    process.env.REACT_APP_FB_NEWSDAY_PAGE_ID,
    process.env.REACT_APP_FB_NEWSDAY_TOKEN
  );

  const totalFollowers = publications.reduce((s, p) => s + (p.totalFollowers || 0), 0);
  const totalConnected = publications.reduce((s, p) => s + (p.configuredPlatforms > 0 ? 1 : 0), 0);
  const platformsLive  = publications.reduce((s, p) => s + (p.configuredPlatforms || 0), 0);

  // Determine which pubs to show Twitter cards for (all known pubs)
  const twitterPubs = ["newsday", "zimind", "standard", "southerneye"];
  const anyTwitterConfigured = twitterPubs.some(k => twitterByPub?.[k]?.configured);

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 0", gap: 12 }}>
        <div style={{ width: 32, height: 32, border: `3px solid ${t.border}`, borderTopColor: "#2563EB", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <div style={{ fontSize: 13, color: t.textMuted }}>Loading social data…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: "#DC262611", border: "1px solid #DC262633", borderRadius: 16, padding: "20px 24px", color: "#DC2626", fontSize: 13 }}>
        Failed to load social data: {error}
      </div>
    );
  }

  return (
    <div>
      {/* KPI summary row */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3,1fr)", gap: 14, marginBottom: 22 }}>
        {[
          { label: "Total Social Followers", value: totalFollowers > 0 ? totalFollowers.toLocaleString() : "—", color: "#2563EB", icon: "🌐", sub: "Across all publications" },
          { label: "Publications Connected", value: `${totalConnected}/4`,   color: "#22c55e", icon: "🔗", sub: `${platformsLive} platform links active` },
          { label: "Top Trending Platform",  value: publications.reduce((top, p) => (p.totalFollowers || 0) > (top?.totalFollowers || 0) ? p : top, null)?.name || "—", color: "#7C3AED", icon: "🏆", sub: "Most followers" },
        ].map((k, i) => (
          <div key={i} style={{ background: t.surface, border: `1px solid ${k.color}22`, borderRadius: 16, padding: "14px 16px", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: k.color }} />
            <div style={{ fontSize: 18, marginBottom: 6 }}>{k.icon}</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: k.color, fontFamily: "Georgia, serif", marginBottom: 2 }}>{k.value}</div>
            <div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8 }}>{k.label}</div>
            <div style={{ fontSize: 10, color: t.textFaint, marginTop: 2 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* No credentials notice */}
      {totalConnected === 0 && (
        <div style={{ background: "#f59e0b11", border: "1px solid #f59e0b33", borderRadius: 14, padding: "14px 18px", marginBottom: 20, display: "flex", alignItems: "flex-start", gap: 10 }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#f59e0b", marginBottom: 3 }}>No social credentials configured</div>
            <div style={{ fontSize: 12, color: t.textSub }}>
              Fill in <code style={{ background: t.surface2, padding: "1px 5px", borderRadius: 4, fontSize: 11 }}>server/.env</code> with your Facebook, Twitter, Instagram, and YouTube API keys.
              See <code style={{ background: t.surface2, padding: "1px 5px", borderRadius: 4, fontSize: 11 }}>SOCIAL_SETUP.md</code> for step-by-step instructions.
            </div>
          </div>
        </div>
      )}

      {/* Publication cards grid */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2,1fr)", gap: 18, marginBottom: 24 }}>
        {publications.map(pub => (
          <PublicationSocialCard
            key={pub.key}
            pub={pub}
            realtimeEntry={byPublication[pub.key] ?? null}
            fbStats={pub.key === "newsday" ? newsdayFb : pub.key === "southerneye" ? southerneyeFb : pub.key === "zimind" ? zimindFb : pub.key === "standard" ? standardFb : null}
            igStats={pub.key === "newsday" ? newsdayIg : null}
            theme={t}
          />
        ))}
        <HstvCard theme={t} />
        {publications.length === 0 && [1, 2, 3, 4].map(i => (
          <div key={i} style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 18, height: 240, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 13, color: t.textFaint }}>Loading…</span>
          </div>
        ))}
      </div>

      {/* Twitter/X section */}
      <div style={{ marginTop: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 16 }}>𝕏</span>
          <span style={{ fontSize: 14, fontWeight: 900, color: t.text, fontFamily: "Georgia, serif" }}>Twitter / X Analytics</span>
          {anyTwitterConfigured
            ? <span style={{ fontSize: 9, color: "#22c55e", background: "#22c55e11", padding: "3px 8px", borderRadius: 20, border: "1px solid #22c55e33", fontWeight: 700 }}>Live</span>
            : <span style={{ fontSize: 9, color: t.textFaint, background: t.surface2, padding: "3px 8px", borderRadius: 20, border: `1px solid ${t.border}` }}>Not Configured</span>
          }
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "repeat(4,1fr)", gap: 14 }}>
          {twitterPubs.map(key => (
            <TwitterStatsCard
              key={key}
              pubKey={key}
              pubName={PUB_LABELS[key] || key}
              pubColor={PUB_COLORS[key]}
              twData={twitterByPub?.[key] ?? null}
              theme={t}
            />
          ))}
        </div>
        {!anyTwitterConfigured && (
          <div style={{ marginTop: 12, fontSize: 11, color: t.textFaint }}>
            Add <code style={{ background: t.surface2, padding: "1px 5px", borderRadius: 4 }}>TWITTER_BEARER_TOKEN_NEWSDAY</code> (and others) to <code style={{ background: t.surface2, padding: "1px 5px", borderRadius: 4 }}>server/.env</code> to enable live Twitter stats.
          </div>
        )}
      </div>
    </div>
  );
}
